import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Search,
  X,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Send,
  Mail,
  MailOpen,
  CheckCircle,
  MousePointerClick,
  Reply,
  AlertCircle,
  Eye,
  Building2,
  UserMinus,
  Loader2,
  Database,
  MoreHorizontal,
} from 'lucide-react';
import {
  CampaignRecipient,
  FilterStatus,
  SortField,
  SortDirection,
} from './types';
import { UICampaignDataset } from '../../../utils/campaignAdapter';

const PAGE_SIZE = 20;

interface RecipientsListProps {
  recipients: CampaignRecipient[];
  totalCount: number;
  statusCounts: Record<FilterStatus, number>;
  filterStatus: FilterStatus;
  searchQuery: string;
  isSearchExpanded: boolean;
  sortField: SortField | null;
  sortDirection: SortDirection;
  expandedRecipients: Set<string>;
  hoveredEngagement: string | null;
  showFilterMenu: boolean;
  searchInputRef: React.RefObject<HTMLInputElement>;
  isLoadingRecipients: boolean;
  currentPage: number;
  selectedIds: Set<string>;
  campaignDatasets?: UICampaignDataset[];
  onFilterChange: (filter: FilterStatus) => void;
  onSearchChange: (query: string) => void;
  onToggleSearchExpanded: () => void;
  onSort: (field: SortField) => void;
  onToggleExpanded: (id: string) => void;
  onHoverEngagement: (id: string | null) => void;
  onToggleFilterMenu: () => void;
  onAddRecipients: () => void;
  onRemoveRecipient?: (recipientId: string) => void;
  onPageChange: (page: number) => void;
  onToggleSelection: (id: string) => void;
  onToggleSelectAll: () => void;
  onBatchRemove: () => void;
  getEngagementInsight: (recipient: CampaignRecipient) => { score: number; pattern: string; opens: number; clicks: number };
  formatTime: (date: string) => string;
  getInitials: (name: string) => string;
}

/**
 * Compact expanded view for a recipient row.
 * Shows email + enrolled date + remove inline.
 * "More details" reveals full contact info, metadata, dataset origin, activity.
 */
const ExpandedRecipientView: React.FC<{
  recipient: CampaignRecipient;
  onRemoveRecipient?: (recipientId: string) => void;
  getActivityIcon: (type: string) => React.ReactNode;
  getActivityLabel: (type: string) => string;
  getDatasetName: (leadTableId?: string) => string | null;
}> = ({ recipient, onRemoveRecipient, getActivityIcon, getActivityLabel, getDatasetName }) => {
  const [showDetails, setShowDetails] = useState(false);

  const hasExtraDetails = !!(
    (recipient.metadata && Object.keys(recipient.metadata).length > 0) ||
    recipient.activity.length > 0
  );

  const datasetName = recipient.leadTableId ? getDatasetName(recipient.leadTableId) : null;

  return (
    <div className="px-3 sm:px-5 pb-2.5 pt-0.5">
      {/* Compact summary: email + dataset + enrolled + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-1.5">
        <div className="flex items-center gap-2 text-[12px] text-neutral-500 flex-1 min-w-0">
          <Mail className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
          <span className="truncate">{recipient.email || 'No email'}</span>
          {datasetName && (
            <>
              <span className="text-neutral-300 flex-shrink-0 hidden sm:inline">·</span>
              <span
                className="hidden sm:flex items-center gap-1 text-neutral-400 flex-shrink-0 max-w-[120px]"
                title={datasetName}
              >
                <Database className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{datasetName}</span>
              </span>
            </>
          )}
          <span className="text-neutral-300 flex-shrink-0 hidden sm:inline">·</span>
          <span className="text-neutral-400 whitespace-nowrap flex-shrink-0 hidden sm:inline">
            {recipient.sentAt
              ? new Date(recipient.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'Not enrolled'}
          </span>
        </div>
        {/* Mobile: show dataset + date on separate line */}
        <div className="flex sm:hidden items-center gap-2 text-[11px] text-neutral-400 ml-5 min-w-0">
          {datasetName && (
            <span className="flex items-center gap-1 max-w-[140px]" title={datasetName}>
              <Database className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{datasetName}</span>
            </span>
          )}
          {datasetName && <span className="text-neutral-300 flex-shrink-0">·</span>}
          <span>
            {recipient.sentAt
              ? new Date(recipient.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'Not enrolled'}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-5 sm:ml-0">
          {hasExtraDetails && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowDetails(!showDetails); }}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-md transition-colors"
            >
              <MoreHorizontal className="w-3 h-3" />
              {showDetails ? 'Less' : 'Details'}
            </button>
          )}
          {onRemoveRecipient && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemoveRecipient(recipient.id); }}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <UserMinus className="w-3 h-3" />
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Full details (toggled) */}
      {showDetails && (
        <div className="mt-1.5 space-y-2.5 pb-1">
          {/* All recipient data from metadata */}
          {recipient.metadata && Object.keys(recipient.metadata).length > 0 && (
            <div className="bg-white rounded-lg border border-neutral-200 p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {Object.entries(recipient.metadata).map(([key, value]) => (
                  <div key={key} className="text-[12px]">
                    <span className="text-neutral-500">{key}: </span>
                    <span className="text-neutral-700">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity timeline */}
          {recipient.activity.length > 0 && (
            <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              <div className="divide-y divide-neutral-100">
                {recipient.activity.map((event, idx) => (
                  <div key={idx} className="flex items-center px-3 py-2 gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      event.type === 'replied' ? 'bg-neutral-900 text-white' :
                      event.type === 'clicked' ? 'bg-neutral-700 text-white' :
                      event.type === 'opened' ? 'bg-neutral-300 text-neutral-600' :
                      event.type === 'bounced' ? 'bg-red-100 text-red-600' :
                      event.type === 'delivered' ? 'bg-neutral-200 text-neutral-500' :
                      'bg-neutral-100 text-neutral-500'
                    }`}>
                      {getActivityIcon(event.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[12px] text-neutral-800 font-medium">{getActivityLabel(event.type)}</span>
                      {event.details && <span className="text-[11px] text-neutral-500 ml-1.5">· {event.details}</span>}
                    </div>
                    <span className="text-[10px] text-neutral-400 flex-shrink-0">
                      {new Date(event.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getStatusLabel = (status: FilterStatus) => {
  const labels: Record<FilterStatus, string> = {
    all: 'All',
    draft: 'Draft',
    queued: 'Queued',
    replied: 'Replied',
    clicked: 'Clicked',
    opened: 'Opened',
    sent: 'Sent',
    bounced: 'Bounced',
  };
  return labels[status];
};

export const RecipientsList: React.FC<RecipientsListProps> = ({
  recipients,
  totalCount,
  statusCounts,
  filterStatus,
  searchQuery,
  isSearchExpanded,
  sortField,
  sortDirection,
  expandedRecipients,
  hoveredEngagement,
  showFilterMenu,
  searchInputRef,
  isLoadingRecipients,
  currentPage,
  selectedIds,
  campaignDatasets,
  onFilterChange,
  onSearchChange,
  onToggleSearchExpanded,
  onSort,
  onToggleExpanded,
  onHoverEngagement,
  onToggleFilterMenu,
  onAddRecipients,
  onRemoveRecipient,
  onPageChange,
  onToggleSelection,
  onToggleSelectAll,
  onBatchRemove,
  getEngagementInsight,
  formatTime,
  getInitials,
}) => {
  const getActivityIcon = (type: string) => {
    const iconClass = "w-3 h-3";
    switch (type) {
      case 'sent': return <Send className={iconClass} />;
      case 'delivered': return <CheckCircle className={iconClass} />;
      case 'opened': return <MailOpen className={iconClass} />;
      case 'clicked': return <MousePointerClick className={iconClass} />;
      case 'replied': return <Reply className={iconClass} />;
      case 'bounced': return <AlertCircle className={iconClass} />;
      default: return <Mail className={iconClass} />;
    }
  };

  const getActivityLabel = (type: string) => {
    switch (type) {
      case 'sent': return 'Email sent';
      case 'delivered': return 'Delivered';
      case 'opened': return 'Opened email';
      case 'clicked': return 'Clicked link';
      case 'replied': return 'Replied';
      case 'bounced': return 'Bounced';
      default: return type;
    }
  };

  const getStatusCount = (status: FilterStatus) => {
    return statusCounts[status];
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);
  // Check if every recipient on the current page is selected
  const allOnPageSelected = recipients.length > 0 && recipients.every(r => selectedIds.has(r.id));

  const getDatasetName = (leadTableId?: string) => {
    if (!leadTableId || !campaignDatasets) return null;
    const dataset = campaignDatasets.find(d => d.id === leadTableId);
    return dataset?.name || null;
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:border-r border-neutral-100">
      <div className="flex-shrink-0 px-3 sm:px-5 py-2.5 sm:py-3 border-b border-neutral-100">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span className="text-[12px] sm:text-[13px] font-medium text-neutral-900">
              Recipients
            </span>
            <span className="text-[11px] sm:text-[12px] text-neutral-400">
              {recipients.length}{searchQuery || filterStatus !== 'all' ? ` of ${totalCount}` : totalCount > 0 ? ` (${totalCount})` : ''}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedIds.size > 0 && (
              <button
                onClick={onBatchRemove}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <UserMinus className="w-3.5 h-3.5" />
                Remove ({selectedIds.size})
              </button>
            )}

            <div className="relative">
              <button
                onClick={onToggleFilterMenu}
                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${
                  filterStatus !== 'all'
                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
                }`}
              >
                <Filter className="w-4 h-4" />
              </button>
              {showFilterMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={onToggleFilterMenu} />
                  <div className="absolute left-0 top-full mt-1 w-40 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-50">
                    {(['all', 'draft', 'queued', 'sent', 'opened', 'clicked', 'replied', 'bounced'] as FilterStatus[]).map(status => (
                      <button
                        key={status}
                        onClick={() => { onFilterChange(status); onToggleFilterMenu(); }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-[12px] hover:bg-neutral-50 ${
                          filterStatus === status ? 'text-neutral-900 font-medium' : 'text-neutral-600'
                        }`}
                      >
                        <span>{getStatusLabel(status)}</span>
                        <span className="text-neutral-400">{getStatusCount(status)}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative flex items-center">
              <div
                className={`flex items-center overflow-hidden transition-all duration-200 ${
                  isSearchExpanded ? 'w-44' : 'w-0'
                }`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onBlur={() => {
                    if (!searchQuery) {
                      onToggleSearchExpanded();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      onSearchChange('');
                      onToggleSearchExpanded();
                    }
                  }}
                  placeholder="Search recipients..."
                  className={`w-full pl-3 pr-2 h-8 text-[12px] bg-neutral-50 border border-neutral-200 rounded-lg transition-all placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-neutral-300 ${
                    isSearchExpanded ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </div>
              <button
                onClick={() => {
                  if (isSearchExpanded && searchQuery) {
                    onSearchChange('');
                  } else {
                    onToggleSearchExpanded();
                    if (!isSearchExpanded) {
                      setTimeout(() => searchInputRef.current?.focus(), 100);
                    }
                  }
                }}
                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all flex-shrink-0 ${
                  searchQuery || isSearchExpanded ? 'bg-blue-50 text-blue-600' : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
                }`}
              >
                {searchQuery ? <X className="w-3.5 h-3.5" /> : <Search className="w-4 h-4" />}
              </button>
            </div>

            <button
              onClick={onAddRecipients}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg ml-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-visible">
        {isLoadingRecipients ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
          </div>
        ) : recipients.length > 0 ? (
          <div>
            {/* Header row with checkbox */}
            <div className="hidden sm:grid grid-cols-[32px,1fr,80px,90px,80px,20px] items-center px-5 py-2.5 bg-neutral-50 border-b border-neutral-200 gap-3 sticky top-0 z-10">
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={onToggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-600 focus:ring-offset-0 cursor-pointer"
                  aria-label="Select all recipients on this page"
                />
              </div>
              {(['name', 'status', 'engagement', 'activity'] as SortField[]).map((field) => (
                <button
                  key={field}
                  onClick={() => onSort(field)}
                  className={`group flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                    field === 'name' ? 'text-left' : field === 'activity' ? 'justify-end w-full' : 'justify-center'
                  } ${sortField === field ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'}`}
                >
                  {field === 'name' ? 'Recipient' : field.charAt(0).toUpperCase() + field.slice(1)}
                  {sortField === field ? (
                    sortDirection === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                  ) : (
                    <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                  )}
                </button>
              ))}
              <div></div>
            </div>

            {/* Recipient rows */}
            {recipients.map(recipient => {
              const isExpanded = expandedRecipients.has(recipient.id);
              const isSelected = selectedIds.has(recipient.id);
              const lastActivity = recipient.activity[recipient.activity.length - 1];
              const engagement = getEngagementInsight(recipient);

              return (
                <div key={recipient.id} className={`border-b border-neutral-100 last:border-0 ${isExpanded ? 'bg-neutral-50/50' : ''}`}>
                  <div
                    className={`sm:grid sm:grid-cols-[32px,1fr,80px,90px,80px,20px] flex flex-col sm:flex-row items-start sm:items-center px-3 sm:px-5 py-2.5 hover:bg-neutral-50 cursor-pointer gap-1.5 sm:gap-3 ${isExpanded ? 'bg-neutral-50' : ''} ${isSelected ? 'bg-blue-50/50' : ''}`}
                    onClick={() => onToggleExpanded(recipient.id)}
                  >
                    {/* Checkbox */}
                    <div className="hidden sm:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelection(recipient.id)}
                        className="w-3.5 h-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-600 focus:ring-offset-0 cursor-pointer"
                        aria-label={`Select ${recipient.name}`}
                      />
                    </div>

                    <div className="flex items-center gap-2.5 min-w-0">
                      {recipient.linkedinUrl ? (
                        recipient.linkedinUrl.includes('/company/') ? (
                          <a
                            href={recipient.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-shrink-0 w-5 h-5 rounded bg-neutral-100 flex items-center justify-center text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700"
                          >
                            <Building2 className="w-3 h-3" />
                          </a>
                        ) : (
                          <a
                            href={recipient.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-shrink-0 text-[#0A66C2] hover:text-[#004182]"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                          </a>
                        )
                      ) : (
                        <div className="w-5 h-5 rounded bg-neutral-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-[8px] font-medium text-neutral-500">{getInitials(recipient.name)}</span>
                        </div>
                      )}
                      <div className="min-w-0 flex items-center gap-1.5">
                        <span className="text-[13px] font-medium text-neutral-900 truncate">{recipient.name}</span>
                        {recipient.name !== recipient.company ? (
                          <span className="text-[11px] text-neutral-400 truncate">· {recipient.company}</span>
                        ) : (
                          <span className="text-[11px] text-neutral-400 truncate">· {recipient.email}</span>
                        )}
                      </div>
                    </div>

                    {/* Mobile status/engagement */}
                    <div className="flex sm:hidden items-center gap-2 ml-7 text-[10px]">
                      <span className={`px-1.5 py-0.5 rounded font-medium capitalize whitespace-nowrap ${
                        recipient.status === 'replied' ? 'bg-neutral-900 text-white' :
                        recipient.status === 'clicked' ? 'bg-neutral-700 text-white' :
                        recipient.status === 'opened' ? 'bg-neutral-200 text-neutral-700' :
                        recipient.status === 'bounced' ? 'bg-red-50 text-red-600' :
                        'bg-neutral-100 text-neutral-500'
                      }`}>
                        {recipient.status}
                      </span>
                      {engagement.pattern && (
                        <span className={`px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                          engagement.pattern === 'Converted' ? 'bg-neutral-900 text-white' :
                          engagement.pattern === 'High intent' ? 'bg-neutral-700 text-white' :
                          engagement.pattern === 'Engaged' ? 'bg-neutral-200 text-neutral-700' :
                          'bg-neutral-100 text-neutral-500'
                        }`}>
                          {engagement.pattern}
                        </span>
                      )}
                      <span className="text-neutral-400">
                        {formatTime(lastActivity?.timestamp ?? recipient.sentAt ?? '')}
                      </span>
                    </div>

                    {/* Desktop status */}
                    <div className="hidden sm:flex items-center justify-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize whitespace-nowrap ${
                        recipient.status === 'replied' ? 'bg-neutral-900 text-white' :
                        recipient.status === 'clicked' ? 'bg-neutral-700 text-white' :
                        recipient.status === 'opened' ? 'bg-neutral-200 text-neutral-700' :
                        recipient.status === 'bounced' ? 'bg-red-50 text-red-600' :
                        'bg-neutral-100 text-neutral-500'
                      }`}>
                        {recipient.status}
                      </span>
                    </div>

                    {/* Desktop engagement */}
                    <div className="hidden sm:flex items-center justify-center">
                      {engagement.pattern ? (
                        <div
                          className="relative inline-flex"
                          onMouseEnter={() => onHoverEngagement(recipient.id)}
                          onMouseLeave={() => onHoverEngagement(null)}
                        >
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium cursor-help whitespace-nowrap ${
                            engagement.pattern === 'Converted' ? 'bg-neutral-900 text-white' :
                            engagement.pattern === 'High intent' ? 'bg-neutral-700 text-white' :
                            engagement.pattern === 'Engaged' ? 'bg-neutral-200 text-neutral-700' :
                            'bg-neutral-100 text-neutral-500'
                          }`}>
                            {engagement.pattern}
                          </span>
                          {hoveredEngagement === recipient.id && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 p-2.5 bg-neutral-900 text-white text-[10px] rounded-lg shadow-xl z-[100]" style={{ pointerEvents: 'none' }}>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[-1px] border-4 border-transparent border-b-neutral-900" />
                              <p className="font-medium mb-1">
                                {engagement.pattern === 'Converted' && 'Replied to your email'}
                                {engagement.pattern === 'High intent' && 'Clicked 3+ times'}
                                {engagement.pattern === 'Engaged' && 'Multiple opens + clicks'}
                                {engagement.pattern === 'Curious' && 'Opens but no clicks'}
                              </p>
                              <p className="text-neutral-400 leading-relaxed">
                                {engagement.pattern === 'Converted' && 'This lead has responded - prioritize follow-up!'}
                                {engagement.pattern === 'High intent' && 'Very interested - ready for direct outreach'}
                                {engagement.pattern === 'Engaged' && 'Showing interest - consider a follow-up'}
                                {engagement.pattern === 'Curious' && 'Reading but hesitant - try a different approach'}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className="relative inline-flex"
                          onMouseEnter={() => onHoverEngagement(recipient.id)}
                          onMouseLeave={() => onHoverEngagement(null)}
                        >
                          <div className="flex items-center gap-1.5 text-[10px] text-neutral-400 cursor-help whitespace-nowrap">
                            {engagement.opens > 0 && (
                              <span className="flex items-center gap-0.5">
                                <Eye className="w-3 h-3" />
                                {engagement.opens}
                              </span>
                            )}
                            {engagement.clicks > 0 && (
                              <span className="flex items-center gap-0.5">
                                <MousePointerClick className="w-3 h-3" />
                                {engagement.clicks}
                              </span>
                            )}
                            {engagement.opens === 0 && engagement.clicks === 0 && '—'}
                          </div>
                          {hoveredEngagement === recipient.id && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 p-2.5 bg-neutral-900 text-white text-[10px] rounded-lg shadow-xl z-[100]" style={{ pointerEvents: 'none' }}>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[-1px] border-4 border-transparent border-b-neutral-900" />
                              <p className="font-medium mb-1">
                                {engagement.opens === 0 && engagement.clicks === 0 && 'No engagement yet'}
                                {engagement.opens > 0 && engagement.clicks === 0 && `Opened ${engagement.opens}x`}
                                {engagement.clicks > 0 && `${engagement.opens}x opens, ${engagement.clicks}x clicks`}
                              </p>
                              <p className="text-neutral-400 leading-relaxed">
                                {engagement.opens === 0 && engagement.clicks === 0 && 'Email delivered but not opened yet'}
                                {engagement.opens > 0 && engagement.clicks === 0 && "Reading your email but hasn't clicked any links"}
                                {engagement.clicks > 0 && engagement.clicks < 3 && 'Showing interest in your content'}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Desktop activity time */}
                    <div className="hidden sm:flex items-center justify-end">
                      <span className="text-[10px] text-neutral-400">
                        {formatTime(lastActivity?.timestamp ?? recipient.sentAt ?? '')}
                      </span>
                    </div>

                    <div className="hidden sm:flex items-center justify-center">
                      <ChevronDown className={`w-4 h-4 text-neutral-300 transition-transform ${isExpanded ? 'rotate-180 text-neutral-500' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded detail view */}
                  {isExpanded && (
                    <ExpandedRecipientView
                      recipient={recipient}
                      onRemoveRecipient={onRemoveRecipient}
                      getActivityIcon={getActivityIcon}
                      getActivityLabel={getActivityLabel}
                      getDatasetName={getDatasetName}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-[13px] text-neutral-400">
              No {filterStatus === 'all' ? '' : filterStatus} recipients
            </p>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      {totalCount > PAGE_SIZE && (
        <div className="flex-shrink-0 flex items-center justify-between px-3 sm:px-5 py-2.5 border-t border-neutral-100 bg-white">
          <span className="text-[11px] text-neutral-500">
            {startItem}–{endItem} of {totalCount}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-[11px] text-neutral-600 px-2">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
